/**
 * Classification: resolves a semantic matrix position ({archetypeId, relevance})
 * to its domain records (archetype, relevance level, historical position data),
 * per the av-methodology-classification spec.
 *
 * Weight is a property of a content item, not of its classification — it is
 * never consulted here (spec: "Weight is independent of classification").
 */

import { archetypes, history2010, methodology } from "./data-source";
import type {
  ArchetypeId,
  Archetype,
  RelevanceId,
  RelevanceLevel,
  HistoricalMatrixPosition,
  MatrixPositionId,
} from "./types";

export class UnknownArchetypeError extends Error {
  constructor(archetypeId: string) {
    super(`Unknown archetypeId: "${archetypeId}" is not one of the 12 historical archetypes.`);
    this.name = "UnknownArchetypeError";
  }
}

export class UnknownRelevanceError extends Error {
  constructor(relevance: string) {
    super(`Unknown relevance level: "${relevance}" is not one of low/medium/high.`);
    this.name = "UnknownRelevanceError";
  }
}

export interface ClassifiedPosition {
  archetype: Archetype;
  relevanceLevel: RelevanceLevel;
  /** Historical (2010) position record, if migrated — may be undefined only if data is incomplete. */
  historical?: HistoricalMatrixPosition;
}

function findArchetype(archetypeId: string): Archetype {
  const archetype = archetypes.find((a) => a.id === archetypeId);
  if (!archetype) throw new UnknownArchetypeError(archetypeId);
  return archetype;
}

function findRelevanceLevel(relevance: string): RelevanceLevel {
  const level = methodology.dimensions.relevance.levels.find((l) => l.id === relevance);
  if (!level) throw new UnknownRelevanceError(relevance);
  return level;
}

/**
 * Resolve a content item's semantic position ({archetypeId, relevance}) to
 * exactly one matrix classification. Rejects unknown archetype/relevance
 * rather than defaulting (av-methodology-classification spec).
 */
export function classify(position: MatrixPositionId): ClassifiedPosition {
  const archetype = findArchetype(position.archetypeId);
  const relevanceLevel = findRelevanceLevel(position.relevance);
  const historical = history2010.matrixPositions.find(
    (p) => p.archetypeId === position.archetypeId && p.relevance === position.relevance,
  );
  return { archetype, relevanceLevel, historical };
}

/**
 * Type guard usable before calling classify(), so callers can validate
 * user input without relying on exceptions for control flow.
 */
export function isKnownArchetypeId(value: string): value is ArchetypeId {
  return archetypes.some((a) => a.id === value);
}

export function isKnownRelevanceId(value: string): value is RelevanceId {
  return methodology.dimensions.relevance.levels.some((l) => l.id === value);
}

/**
 * A node's weight (1|2|3) is stored/read independently of its classification
 * and never influences which position it resolves to (works for either
 * ContentNode or InfluenceNode, both of which carry a `weight` field).
 */
export function withWeight<T extends { weight: 1 | 2 | 3 }>(item: T, weight: 1 | 2 | 3): T {
  return { ...item, weight };
}
